import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  SwapHoriz as TransferIcon,
  Home as RoomIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader, Loading, EmptyState } from '../components/Layout';
import api from '../services/api';
import tenantService from '../services/tenantService';
import roomService from '../services/roomService';

const Transfers = () => {
  const [transfers, setTransfers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [transferType, setTransferType] = useState('room'); // 'room' or 'deposit'
  const [formData, setFormData] = useState({
    tenant_id: '',
    from_room_id: '',
    to_room_id: '',
    transfer_date: new Date().toISOString().split('T')[0],
    reason: '',
    notes: '',
    balance_transfer: true,
    additional_deposit: '',
  });

  useEffect(() => {
    fetchTransfers();
    fetchTenants();
    fetchRooms();
  }, []);

  const fetchTransfers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/transfers');
      setTransfers(response.data || []);
    } catch (error) {
      if (error.response?.status === 404) {
        // Endpoint not implemented yet
        console.log('Transfers endpoint not available yet');
      } else {
        console.error('Failed to load transfers:', error);
      }
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await tenantService.getAllTenants();
      setTenants(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await roomService.getAllRooms();
      setRooms(response.data);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  };

  const handleOpenDialog = (type) => {
    setTransferType(type);
    setFormData({
      tenant_id: '',
      from_room_id: '',
      to_room_id: '',
      transfer_date: new Date().toISOString().split('T')[0],
      reason: '',
      notes: '',
      balance_transfer: true,
      additional_deposit: '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmitRoomTransfer = async () => {
    if (!formData.tenant_id || !formData.from_room_id || !formData.to_room_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.from_room_id === formData.to_room_id) {
      toast.error('Source and destination rooms must be different');
      return;
    }

    try {
      const payload = {
        tenant_id: formData.tenant_id,
        from_room_id: formData.from_room_id,
        to_room_id: formData.to_room_id,
        transfer_date: formData.transfer_date,
        reason: formData.reason,
        notes: formData.notes,
        balance_transfer: formData.balance_transfer,
      };
      if (formData.additional_deposit && parseFloat(formData.additional_deposit) > 0) {
        payload.additional_deposit = parseFloat(formData.additional_deposit);
      }
      const response = await api.post('/api/transfers/room', payload);
      const data = response.data;
      let msg = 'Room transfer completed successfully.';
      if (data.invoices_moved > 0) {
        msg += ` ${data.invoices_moved} invoice(s) moved to new room.`;
      }
      if (data.deposit_added > 0) {
        msg += ` KES ${data.deposit_added.toLocaleString()} deposit added.`;
      }
      toast.success(msg, { autoClose: 8000 });
      handleCloseDialog();
      fetchTransfers();
      fetchRooms();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Transfer feature is not yet available. Please contact your system administrator.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to process room transfer');
      }
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    if (transferType === 'room') {
      handleSubmitRoomTransfer();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTransferTypeColor = (type) => {
    return type === 'room' ? 'primary' : 'secondary';
  };

  const filteredTransfers = transfers.filter(t => {
    if (tabValue === 0) return true; // All
    if (tabValue === 1) return t.transfer_type === 'room';
    if (tabValue === 2) return t.transfer_type === 'deposit';
    return true;
  });

  const stats = {
    total: transfers.length,
    roomTransfers: transfers.filter(t => t.transfer_type === 'room').length,
    depositTransfers: transfers.filter(t => t.transfer_type === 'deposit').length,
  };

  const getTenantCurrentRoom = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant && tenant.current_room_id) {
      const room = rooms.find(r => r.id === tenant.current_room_id);
      return room?.room_number || 'N/A';
    }
    return 'None';
  };

  if (loading) {
    return <Loading message="Loading transfers..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Transfers"
        subtitle="Manage tenant room and deposit transfers"
        breadcrumbs={[{ label: 'Transfers' }]}
      />

      {/* Quick Action Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => handleOpenDialog('room')}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <RoomIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6">Room Transfer</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Move tenant to different room
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Transfers
              </Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Room Transfers
              </Typography>
              <Typography variant="h4" color="primary.main">
                {stats.roomTransfers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="All Transfers" />
        <Tab label="Room Transfers" />
      </Tabs>

      {/* Transfers Table */}
      <Paper sx={{ p: 3 }}>
        {filteredTransfers.length === 0 ? (
          <EmptyState
            icon={TransferIcon}
            title="No Transfers"
            description="No transfer records found"
            action={
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('room')}
              >
                Create Room Transfer
              </Button>
            }
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>From Room</TableCell>
                  <TableCell>To Room</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Balance Transferred</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransfers.map((transfer) => (
                  <TableRow key={transfer.id}>
                    <TableCell>{formatDate(transfer.transfer_date)}</TableCell>
                    <TableCell>
                      <Chip
                        label={transfer.transfer_type}
                        color={getTransferTypeColor(transfer.transfer_type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {tenants.find(t => t.id === transfer.tenant_id)?.full_name || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {rooms.find(r => r.id === transfer.from_room_id)?.room_number || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {rooms.find(r => r.id === transfer.to_room_id)?.room_number || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {transfer.reason || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {transfer.balance_transferred ? (
                        <Chip label="Yes" color="success" size="small" />
                      ) : (
                        <Chip label="No" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Transfer Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {transferType === 'room' ? 'Room Transfer' : 'Deposit Transfer'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {transferType === 'room' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Select Tenant *"
                    name="tenant_id"
                    value={formData.tenant_id}
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value="">-- Select Tenant --</MenuItem>
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.full_name} - Current Room: {getTenantCurrentRoom(tenant.id)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="From Room *"
                    name="from_room_id"
                    value={formData.from_room_id}
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value="">-- Select Room --</MenuItem>
                    {rooms
                      .filter(r => r.status === 'occupied')
                      .map((room) => (
                        <MenuItem key={room.id} value={room.id}>
                          {room.room_number} - {room.property_name}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="To Room *"
                    name="to_room_id"
                    value={formData.to_room_id}
                    onChange={handleInputChange}
                    required
                    helperText="Only vacant rooms are shown"
                  >
                    <MenuItem value="">-- Select Room --</MenuItem>
                    {rooms
                      .filter(r => r.status === 'vacant' || r.status === 'maintenance')
                      .map((room) => (
                        <MenuItem key={room.id} value={room.id}>
                          {room.room_number} - {room.property_name} (KES {room.monthly_rent})
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Transfer Date"
                    name="transfer_date"
                    type="date"
                    value={formData.transfer_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    placeholder="Reason for transfer"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Additional Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Additional Deposit (KES)"
                    name="additional_deposit"
                    type="number"
                    value={formData.additional_deposit}
                    onChange={handleInputChange}
                    helperText="Top-up deposit if new room requires more. Leave empty if no change."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <input
                      type="checkbox"
                      name="balance_transfer"
                      checked={formData.balance_transfer}
                      onChange={handleInputChange}
                      style={{ marginRight: 8 }}
                    />
                    <Typography variant="body2">
                      Transfer outstanding balances to new room
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={<TransferIcon />}
          >
            Process Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Transfers;
